// AgentOS rule for the nQube runner: a model may propose, a non-model gate
// must sit on every path to a side effect, and the side effect must name a
// capability declared on the pipeline. Copied to
// frontend/src/data/agentosGraph.js by scripts/generate-entities.mjs.
// No Node imports — the browser copy has to stay runnable.

// pipeline.call counts as a model block: the callee may run models, and its
// own graph is validated separately (with its own capabilities) at call time.
const MODEL_TYPES = new Set([
  'transform.llm', 'transform.retrieve', 'loop.foreach', 'loop.refine', 'loop.until', 'control.coordinate', 'pipeline.call', 'control.decision',
]);

const KIND_BY_TYPE = {
  'output.s3write': 's3',
  'output.dataset': 'dataset',
  'output.knowledge': 'knowledge',
  'container.run': 'container',
  'control.preflight': 'container', // launches a container too — same capability binding
  'code.python': 'code', // user code on a preset image — capability target = preset name or '*'
  'output.webhook': 'webhook',
};

const SECRET_KEY = /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,63}$/;

// The policy gate is VERSIONED: bump when a rule below changes meaning
// (what counts as a model block, a gate, a valid capability binding, the
// prod rule). Every run plan, commit and audit event records the version
// it was judged under, so a later rule change is distinguishable from a
// pipeline that was always out of policy.
export const AGENTOS_POLICY_VERSION = '2026.10.28';
export const AGENTOS_POLICY_RULES = [
  'model→side-effect paths cross a non-model gate (control.review, control.filter in keyword mode, control.switch, logic.validate, or control.preflight)',
  'every side effect names a capability declared on the pipeline; kinds must match block types (code.python is a `code` side effect bound to a preset)',
  'webhook capabilities bind to an enabled Integration in the run env whose baseUrl prefixes the target',
  'in prod only control.review gates a model→side-effect path',
  'pipeline.call and loop.* are model blocks; callee graphs are judged separately with their own capabilities',
];

export function sideEffectKind(type) {
  return KIND_BY_TYPE[type] || null;
}

export function isModelBlock(block) {
  if (!block) return false;
  if (MODEL_TYPES.has(block.type)) return true;
  return block.type === 'control.filter' && (block.config?.mode || 'contains') === 'llm';
}

// Org policy the graph is judged under (optional — every caller that omits
// it gets the pure in-graph rules):
//   env           'sandbox' | 'uat' | 'prod'. In prod only a HUMAN review
//                 gates a model→side-effect path; a keyword filter is not
//                 enough (Administration's environment controls).
//   integrations  the org's Integration catalog (admin-owned). When given,
//                 every webhook capability must bind to an enabled
//                 integration (capability.integrationId) whose scope is this
//                 env and whose baseUrl prefixes the capability target — a
//                 builder cannot point a pipeline at an arbitrary URL.
export const ENVS = ['sandbox', 'uat', 'prod'];

export function isGateBlock(block, policy) {
  if (!block) return false;
  if (block.type === 'control.review') return true;
  if (policy?.env === 'prod') return false;
  if (block.type === 'control.switch') return true;
  if (block.type === 'logic.validate') return true; // rule check on the record, no model
  if (block.type === 'control.preflight') return true; // deterministic checks in a container, no model
  return block.type === 'control.filter' && (block.config?.mode || 'contains') !== 'llm';
}

// Output ports of a block: static (catalog `ports`) or derived from config
// (switch rules, decision options) — the editor draws one handle per port
// and the runner routes a ported output down the matching edges only.
export function portsOf(block, def) {
  if (!block) return null;
  if (def?.dynamicPorts === 'rules') {
    const names = [];
    for (const line of String(block.config?.rules || '').split('\n')) {
      const m = line.match(/^\s*([A-Za-z0-9_-]{1,32})\s*:/);
      if (m && !names.includes(m[1])) names.push(m[1]);
    }
    const dflt = String(block.config?.default || 'default').trim() || 'default';
    if (!names.includes(dflt)) names.push(dflt);
    return names;
  }
  if (def?.dynamicPorts === 'options') {
    const names = String(block.config?.options || '').split(/[,\n]/).map((o) => o.trim()).filter(Boolean);
    return names.length ? [...new Set(names)] : null;
  }
  return Array.isArray(def?.ports) && def.ports.length > 1 ? def.ports : null;
}

function labelOf(block) {
  return block?.label || block?.type || 'block';
}

function capabilityList(capabilities) {
  return Array.isArray(capabilities) ? capabilities : [];
}

// A capability against the org's Integration catalog (null = fine).
export function integrationProblem(cap, policy) {
  if (!policy || !Array.isArray(policy.integrations)) return null;
  if (cap?.kind !== 'webhook') return null;
  const env = ENVS.includes(policy.env) ? policy.env : 'sandbox';
  if (!cap.integrationId) return `capability '${cap.id}' must bind to an org Integration (Administration → Integrations) — pick one in the capability panel`;
  const integ = policy.integrations.find((i) => i && i.id === cap.integrationId);
  if (!integ) return `capability '${cap.id}' binds to integration '${cap.integrationId}', which is not in this org`;
  if (integ.enabled === false) return `capability '${cap.id}' binds to disabled integration '${integ.name || integ.id}'`;
  const scope = ENVS.includes(integ.scope) ? integ.scope : 'sandbox';
  if (scope !== env) return `capability '${cap.id}' binds to a ${scope} integration but this pipeline runs in ${env}`;
  const base = String(integ.baseUrl || '').replace(/\/+$/, '');
  if (!base) return `integration '${integ.name || integ.id}' has no base URL`;
  if (!String(cap.target || '').startsWith(base)) return `capability '${cap.id}' target must start with integration base URL ${base}`;
  return null;
}

// One side-effect block against the manifest. `resolved.key` is the object
// key the handler will actually write (defaults differ from the config).
export function capabilityProblem(block, capabilities, resolved = {}, policy = null) {
  const kind = sideEffectKind(block?.type);
  if (!kind) return null;
  const label = labelOf(block);
  const caps = capabilityList(capabilities);
  const id = block.config?.capabilityId;
  const cap = caps.find((c) => c && c.id === id);
  if (!cap) return `${label} names no declared capability`;
  if (cap.kind !== kind) return `${label} capability '${cap.id}' is ${cap.kind || 'unset'}, need ${kind}`;
  const target = String(cap.target || '');
  if (!target) return `${label} capability '${cap.id}' has no target`;
  const bound = integrationProblem(cap, policy);
  if (bound) return `${label}: ${bound}`;
  if (kind === 's3') {
    const key = resolved.key != null ? String(resolved.key) : String(block.config?.key || '');
    if (key && !key.startsWith(target)) return `${label} key must start with capability target '${target}'`;
    if (!key && target !== 'pipelines' && target !== 'pipelines/') {
      return `${label} uses the default pipelines/ key; set the capability target to pipelines/ or set a key under '${target}'`;
    }
  } else if (kind === 'dataset') {
    if (String(block.config?.datasetId || '') !== target) return `${label} datasetId must equal capability target '${target}'`;
  } else if (kind === 'knowledge') {
    const audience = block.config?.audience === 'internal' ? 'internal' : 'public';
    if (audience !== target) return `${label} audience must equal capability target '${target}'`;
  } else if (kind === 'container') {
    const image = String(block.config?.image || '');
    if (!image.startsWith(target)) return `${label} image must start with capability target '${target}'`;
  } else if (kind === 'code') {
    const preset = String(block.config?.preset || 'python-ds');
    if (target !== '*' && preset !== target) return `${label} preset '${preset}' must equal capability target '${target}' (or target *)`;
  } else if (kind === 'webhook') {
    let url;
    try { url = new URL(target); } catch { return `${label} webhook target is not a URL`; }
    if (url.protocol !== 'https:') return `${label} webhook target must be https`;
    if (!SECRET_KEY.test(String(cap.secretKey || ''))) {
      return `${label} webhook capability needs a secretKey name (SSM pipeline-secrets/<name>), not the secret itself`;
    }
  }
  return null;
}

export function capabilityFor(block, capabilities, resolved, policy) {
  const problem = capabilityProblem(block, capabilities, resolved, policy);
  if (problem) throw new Error(problem);
  return capabilityList(capabilities).find((c) => c && c.id === block.config?.capabilityId);
}

// Every problem that should refuse a run. Empty means the graph may run.
export function agentOsProblems(graph, capabilities, policy = null) {
  const blocks = graph?.blocks || [];
  const conns = graph?.connections || [];
  const byId = Object.fromEntries(blocks.map((b) => [b.id, b]));
  const outs = Object.fromEntries(blocks.map((b) => [b.id, []]));
  for (const c of conns) {
    if (outs[c.from]) outs[c.from].push(c.to);
  }
  const problems = [];
  for (const block of blocks) {
    const problem = capabilityProblem(block, capabilities, {}, policy);
    if (problem) problems.push(problem);
    if (block.type === 'pipeline.call' && !String(block.config?.pipelineId || '').trim()) {
      problems.push(`${labelOf(block)} names no pipeline to call (set pipelineId, or "self")`);
    }
  }
  const prodNote = policy?.env === 'prod' ? ' (prod: only a human review counts as a gate)' : '';
  for (const model of blocks.filter(isModelBlock)) {
    const seen = new Set();
    const stack = [[model.id, false]];
    while (stack.length) {
      const [id, gated] = stack.pop();
      const key = `${id}:${gated ? 1 : 0}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const node = byId[id];
      if (!node) continue;
      // A gate that is also a container launch (control.preflight: an
      // allowlisted image with no credentials, checking before the spend)
      // is judged as the gate, not as the write it protects.
      if (id !== model.id && sideEffectKind(node.type) && !gated && !isGateBlock(node, policy)) {
        problems.push(
          `${labelOf(model)} can reach ${labelOf(node)} with no gate — put a human review or a keyword filter on that path${prodNote}`
        );
      }
      const next = gated || isGateBlock(node, policy);
      for (const to of outs[id] || []) stack.push([to, next]);
    }
  }
  return [...new Set(problems)];
}

export function assertAgentOsGraph(graph, capabilities, policy = null) {
  const problems = agentOsProblems(graph, capabilities, policy);
  if (problems.length) throw new Error(problems.join('; '));
}
